import { setup, assign, ActorRefFrom } from 'xstate';

/**
 * Dynamic object states
 */
export type DynamicObjectState = 
  | { value: 'intact'; context: DynamicObjectContext }
  | { value: 'breaking'; context: DynamicObjectContext }
  | { value: 'debris'; context: DynamicObjectContext }
  | { value: 'cleanup'; context: DynamicObjectContext };

/**
 * Context for dynamic object state machine
 */
export interface DynamicObjectContext {
  objectId: string;
  behaviorId: string;
  health: number;
  maxHealth: number;
  accumulatedDamage: number;
  breakThreshold: number;
  debrisLifetime: number;
  debrisTimer: number;
}

/**
 * Events for dynamic object state machine
 */
export type DynamicObjectEvent =
  | { type: 'DAMAGE_RECEIVED'; amount: number; impactForce: number; impactDirection: [number, number, number] }
  | { type: 'BREAK_THRESHOLD_REACHED' }
  | { type: 'DEBRIS_TIMEOUT' }
  | { type: 'FORCE_CLEANUP' };

/**
 * Dynamic object state machine
 * Manages lifecycle: intact → breaking → debris → cleanup
 */
export const dynamicObjectMachine = setup({
  types: {
    context: {} as DynamicObjectContext,
    events: {} as DynamicObjectEvent,
    input: {} as DynamicObjectContext
  },
  
  guards: {
    shouldBreak: ({ context, event }) => {
      if (event.type !== 'DAMAGE_RECEIVED') return false;
      const newDamage = context.accumulatedDamage + event.amount;
      return newDamage >= context.breakThreshold;
    },
    
    debrisExpired: ({ context }) => {
      return context.debrisTimer >= context.debrisLifetime;
    }
  },
  
  actions: {
    accumulateDamage: assign({
      accumulatedDamage: ({ context, event }) => {
        if (event.type !== 'DAMAGE_RECEIVED') return context.accumulatedDamage;
        return context.accumulatedDamage + event.amount;
      },
      health: ({ context, event }) => {
        if (event.type !== 'DAMAGE_RECEIVED') return context.health;
        return Math.max(0, context.health - event.amount);
      }
    }),
    
    startBreaking: assign({
      accumulatedDamage: ({ context }) => context.breakThreshold
    }),
    
    updateDebrisTimer: assign({
      debrisTimer: ({ context }) => context.debrisTimer + 0.016 // Assume ~60fps
    }),
    
    resetDebrisTimer: assign({
      debrisTimer: 0
    })
  }
  
}).createMachine({
  id: 'dynamicObject',
  
  initial: 'intact',
  
  context: ({ input }: { input: DynamicObjectContext }) => input,
  
  states: {
    intact: {
      on: {
        DAMAGE_RECEIVED: [
          {
            guard: 'shouldBreak',
            target: 'breaking',
            actions: ['accumulateDamage', 'startBreaking']
          },
          {
            actions: 'accumulateDamage'
          }
        ]
      }
    },
    
    breaking: {
      entry: 'startBreaking',
      
      after: {
        100: 'debris' // Short breaking animation duration
      }
    },
    
    debris: {
      entry: 'resetDebrisTimer',
      
      on: {
        DEBRIS_TIMEOUT: 'cleanup',
        FORCE_CLEANUP: 'cleanup'
      },
      
      // Could add periodic timer updates here if needed
    },
    
    cleanup: {
      type: 'final'
    }
  }
});

export interface DynamicObjectDebugSnapshot {
  objectId: string;
  state: string;
  health: number;
  context: DynamicObjectContext;
}

export function getDynamicObjectDebugSnapshot(actor: ActorRefFrom<typeof dynamicObjectMachine>): DynamicObjectDebugSnapshot {
  const snapshot = actor.getSnapshot();
  return {
    objectId: snapshot.context.objectId,
    state: snapshot.value as string,
    health: snapshot.context.health,
    context: snapshot.context
  };
}
